import { useEditorStore } from "components/Editor/useEditorStore";
import { useEnvironmentInterface } from "environment-interface";
import { useBranchEditor } from "features/editor/Feature";
import { usePitcher } from "features/utils/usePitcher";
import { useCallback } from "react";

import {
  getPullRequestUrl,
  getNewPullRequestUrl,
  getContributeToParentUrl,
} from "utils/git";

import { useAnalytics } from "./useAnalytics";

export const useOpenPullRequestURL = (): (() => Promise<void>) => {
  const { navigation } = useEnvironmentInterface();
  const {
    owner,
    repo,
    branch: name,
    contribution,
    defaultBranchName,
    pullRequest,
  } = useBranchEditor();
  const { git } = useEditorStore();
  const pitcher = usePitcher();
  const track = useAnalytics();

  const openUrl = useCallback(async () => {
    if (
      !git.status.remote.head &&
      pitcher.hasFeature("REMOTE_BRANCH_PRESENCE_DETECTION")
    ) {
      await pitcher.clients.git.push();
    }

    /**
     * Create regular PR
     */
    const firstCommit = git.status.commits[git.status.commits.length - 1];

    let pullRequestURL = getNewPullRequestUrl({
      owner,
      repo,
      sourceBranch: name,
      targetBranch: git.targetBranchName,
      commitMessage: firstCommit.message,
    });

    /**
     * Open pull request, which has already been created
     */
    if (pullRequest) {
      pullRequestURL = getPullRequestUrl({
        owner,
        repo,
        prNumber: pullRequest.number,
      });
    }

    /**
     * Contribution branch or Regular fork
     */
    if (!pullRequest && contribution) {
      const remotes = pitcher.clients.git.getRemotes();

      const contributeUrl = getContributeToParentUrl({
        fork: remotes.origin,
        forkBranchName: name,
        parent: remotes.upstream,
        parentBranchName: defaultBranchName,
      });

      if (contributeUrl) {
        pullRequestURL = contributeUrl;
      }
    }

    navigation.openUrl(pullRequestURL);

    track("git_open_url_pr");
  }, [
    owner,
    repo,
    name,
    contribution,
    defaultBranchName,
    pullRequest,
    git.status,
  ]);

  return openUrl;
};
